import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { fmtNum } from "@/lib/format";

function Grid({ title, quotes }) {
  return (
    <div className="surface p-6">
      <div className="kicker mb-4">{title}</div>
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-muted-foreground text-xs uppercase tracking-widest">
            <th className="pb-3">Symbol</th>
            <th className="pb-3 text-right">Price</th>
            <th className="pb-3 text-right">Change</th>
          </tr>
        </thead>
        <tbody>
          {(quotes || []).map((q) => (
            <tr key={q.symbol} className="border-t border-border row-hover">
              <td className="py-3 font-mono">{q.symbol}</td>
              <td className="py-3 text-right font-mono">{fmtNum(q.price, 4)}</td>
              <td className={`py-3 text-right font-mono ${q.change_pct >= 0 ? "pos" : "neg"}`}>{q.change_pct >= 0 ? "+" : ""}{fmtNum(q.change_pct, 3)}%</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const cellClass = (v) => v == null ? "text-muted-foreground" : v >= 0 ? "pos" : "neg";
const cellText = (v) => v == null ? "—" : `${v >= 0 ? "+" : ""}${Number(v).toFixed(1)}%`;

function IndicesAnnualTable() {
  const [data, setData] = useState({ years: [], indices: [], current_year: null, ytd_source: "" });
  const [fund, setFund] = useState({ years: [], returns: [], ytd_pct: null, fund_name: "", fund_code: "" });
  useEffect(() => {
    api.get("/markets/indices-annual").then((r) => setData(r.data));
    api.get("/markets/fund-annual").then((r) => setFund(r.data));
  }, []);
  const rangeLabel = data.years.length ? `${data.years[0]}–${data.years[data.years.length - 1]}` : "";
  // Align fund returns to data.years (fund data may not cover every year)
  const fundYearMap = Object.fromEntries((fund.years || []).map((y, i) => [y, fund.returns[i]]));
  return (
    <div className="surface p-6 overflow-x-auto" data-testid="indices-annual">
      <div className="flex items-baseline justify-between mb-4 flex-wrap gap-2">
        <div>
          <div className="kicker mb-1">Global Indices · Annual Total Return · YTD Live via TwelveData</div>
          <h3 className="text-2xl">Calendar-Year Performance · {rangeLabel}</h3>
        </div>
        <div className="text-xs text-muted-foreground font-mono">
          Rolling {data.years.length}-year window · YTD: {data.ytd_source || "loading"}
        </div>
      </div>
      <table className="w-full text-sm">
        <thead>
          <tr className="text-muted-foreground text-xs uppercase tracking-widest">
            <th className="text-left pb-3 pr-4 sticky left-0 bg-card">Index</th>
            {data.years.map((y) => (
              <th key={y} className={`text-right pb-3 pl-3 ${y === data.current_year ? "gold" : ""}`}>
                {y}{y === data.current_year ? "*" : ""}
              </th>
            ))}
            <th className="text-right pb-3 pl-4 border-l border-border gold">YTD Live</th>
          </tr>
        </thead>
        <tbody>
          {/* Fund highlight row */}
          <tr data-testid="fund-annual-row" className="border-y-2 border-primary/60 bg-primary/5">
            <td className="py-3 pr-4 sticky left-0 bg-card">
              <div className="text-sm gold font-medium">{fund.fund_name || "Piplight Fund"}</div>
              <div className="kicker text-[10px] gold">{fund.fund_code || "PL"} · net of fees</div>
            </td>
            {data.years.map((y) => {
              const v = fundYearMap[y];
              return (
                <td key={y} className={`py-3 pl-3 text-right font-mono font-medium ${cellClass(v)}`}>
                  {cellText(v)}
                </td>
              );
            })}
            <td className={`py-3 pl-4 text-right font-mono font-medium border-l border-border ${cellClass(fund.ytd_pct)}`}>
              {cellText(fund.ytd_pct)}
            </td>
          </tr>
          {data.indices.map((idx) => (
            <tr key={idx.code} className="border-t border-border row-hover">
              <td className="py-2 pr-4 sticky left-0 bg-card">
                <div className="text-sm">{idx.name}</div>
                <div className="kicker text-[10px]">{idx.code}</div>
              </td>
              {idx.returns.map((v, i) => (
                <td key={i} className={`py-2 pl-3 text-right font-mono ${cellClass(v)}`}>
                  {cellText(v)}
                </td>
              ))}
              <td className={`py-2 pl-4 text-right font-mono border-l border-border ${cellClass(idx.ytd_pct)}`}>
                {cellText(idx.ytd_pct)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <p className="text-xs text-muted-foreground mt-4 leading-relaxed max-w-3xl">
        * Current-year column and the YTD Live column are refreshed daily from TwelveData
        time-series. Prior years are locked calendar-year total returns in local currency.
        The table advances one year each year-end and drops the oldest year to preserve the
        rolling window length. The highlighted row is the selected Piplight fund's own net-of-fees
        return computed from that fund's audited NAV series.
      </p>
    </div>
  );
}

function TopHedgeFundsTable() {
  const [data, setData] = useState({ year: null, funds: [], source: "" });
  useEffect(() => { api.get("/markets/top-hedge-funds").then((r) => setData(r.data)); }, []);
  return (
    <div className="surface p-6" data-testid="top-hedge-funds">
      <div className="flex items-baseline justify-between mb-4 flex-wrap gap-2">
        <div>
          <div className="kicker mb-1">Peer Universe · {data.source === "database" ? "curated by super-admin" : "public composites"}</div>
          <h3 className="text-2xl">Top 10 Hedge Funds · Last Year ({data.year})</h3>
        </div>
      </div>
      <table className="w-full text-sm">
        <thead>
          <tr className="text-muted-foreground text-xs uppercase tracking-widest">
            <th className="text-left pb-3">#</th>
            <th className="text-left pb-3">Fund</th>
            <th className="text-left pb-3">Manager</th>
            <th className="text-right pb-3">Return · {data.year}</th>
          </tr>
        </thead>
        <tbody>
          {data.funds.map((f, i) => (
            <tr key={`${f.rank || i}-${f.name}`} className="border-t border-border row-hover">
              <td className="py-3 font-mono text-xs text-muted-foreground">{String(f.rank || i + 1).padStart(2, "0")}</td>
              <td className="py-3">{f.name}</td>
              <td className="py-3 font-mono text-xs text-muted-foreground">{f.manager}</td>
              <td className={`py-3 text-right font-mono ${f.return_pct >= 0 ? "pos" : "neg"}`}>
                {f.return_pct >= 0 ? "+" : ""}{Number(f.return_pct).toFixed(1)}%
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <p className="text-xs text-muted-foreground mt-4 leading-relaxed max-w-3xl">
        Composite returns are curated by Piplight super-admins from public reporting (HFR / Morningstar / prime-broker letters). To refresh the list at year-end, super-admins can edit each row from Super Admin → Peer Composites.
      </p>
    </div>
  );
}

export default function Markets() {
  const [m, setM] = useState(null);
  useEffect(() => {
    const load = () => api.get("/markets/snapshot").then((r) => setM(r.data));
    load();
    const id = setInterval(load, 15000);
    return () => clearInterval(id);
  }, []);
  return (
    <div className="container-tight py-16 space-y-12">
      <div>
        <div className="kicker mb-3">Real-Time Markets & Peer Comparison</div>
        <h1 className="text-5xl mb-4">Live market context.</h1>
        <p className="text-muted-foreground max-w-2xl mb-8 leading-relaxed">
          A cross-section of the instrument universe traded across the Piplight FX Multi-Asset book,
          followed by a rolling record of global equity-index total returns and a look at the
          institutional hedge-fund peer group.
        </p>
        <div className="text-xs text-muted-foreground mb-6 font-mono">
          Source: {m?.source || "loading"} · Updated {m?.as_of || "—"}
        </div>
        <div className="grid md:grid-cols-2 gap-6">
          <Grid title="FX Majors" quotes={m?.fx} />
          <Grid title="Metals" quotes={m?.metals} />
          <Grid title="Global Indices" quotes={m?.indices} />
          <Grid title="Digital Assets" quotes={m?.crypto} />
        </div>
      </div>

      <IndicesAnnualTable />
      <TopHedgeFundsTable />

      <div className="text-xs text-muted-foreground leading-relaxed max-w-3xl">
        <div className="kicker mb-2">Performance Advertising Note (SEC Marketing Rule)</div>
        Any fund-level performance figures displayed on this platform are net of applicable fees and refer to standardized 1, 5, and 10-year periods. No cherry-picked periods are shown. Past performance is not indicative of future results.
      </div>
    </div>
  );
}
